import React, { useMemo, useState } from "react";
import { DataGrid } from "@mui/x-data-grid";
import moment from "moment";
import NoRecordsFound from "../../../components/no-records-found";
import { Box, Chip } from "@mui/material";

const TeacherWalletTable = ({
  transactionsData = [],
  tab,
  selectedOfferSlotIds,
  setSelectedOfferSlotIds,
  setTotalTransferAmount,
}) => {
  const columns = useMemo(() => {
    switch (tab) {
      case "inbound":
        return [
          {
            field: "createdAt",
            headerName: "Date",
            flex: 1,
            minWidth: 110,
            valueGetter: (row) => moment(row).format("MMM Do YY"),
          },
          {
            field: "transfered",
            headerName: "Payment Status",
            flex: 1.5,
            minWidth: 150,
            renderCell: ({ row }) => (
              <Chip
                label={row?.transfered ? "PAID" : "PENDING"}
                color={row?.transfered ? "success" : "warning"}
                size="small"
                sx={{ mr: 0.5, mb: 0.5 }}
              />
            ),
          },
          {
            field: "studentName",
            headerName: "Student",
            flex: 1.5,
            minWidth: 150,
            renderCell: ({ row }) => row?.studentName || "N/A",
          },
          {
            field: "subjectInfo",
            headerName: "Subject",
            flex: 2,
            minWidth: 180,
            renderCell: ({ row }) => {
              return (
                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    justifyContent: "flex-start",
                    alignItems: "center",
                    width: "100%",
                    height: "100%",
                    padding: "4px",
                  }}
                >
                  {row?.subjectInfo?.map((subj, index) => (
                    <Chip
                      key={index}
                      label={subj?.subjectName}
                      color="primary"
                      size="small"
                      sx={{ mr: 0.5, mb: 0.5 }}
                    />
                  ))}
                </Box>
              );
            },
          },
          {
            field: "status",
            headerName: "Status",
            flex: 1,
            minWidth: 100,
            renderCell: ({ row }) => (
              <Chip
                label={row?.status || "N/A"}
                color={row?.status === "completed" ? "success" : "error"}
                size="small"
              />
            ),
          },
          {
            field: "amount",
            headerName: "Amount",
            flex: 1,
            minWidth: 100,
          },
          {
            field: "currency",
            headerName: "Currency",
            flex: 1,
            minWidth: 100,
          },
          {
            field: "channel",
            headerName: "Channel",
            flex: 1.5,
            minWidth: 120,
            valueGetter: () => "Bank Alfalah",
          },
        ];

      case "outbound":
        return [
          {
            field: "date",
            headerName: "Date",
            flex: 1,
            minWidth: 110,
            valueGetter: (row) => moment(row?.date).format("MMM Do YY"),
          },
          {
            field: "amount",
            headerName: "Amount",
            flex: 1,
            minWidth: 80,
          },
          {
            field: "currency",
            headerName: "Currency",
            flex: 1,
            minWidth: 80,
          },
          {
            field: "status",
            headerName: "Status",
            flex: 1,
            minWidth: 80,
            renderCell: () => (
              <Chip
                label="PAID"
                color="success"
                size="small"
                sx={{ mr: 0.5, mb: 0.5 }}
              />
            ),
          },
        ];

      case "merge":
        return [
          {
            field: "date",
            headerName: "Date",
            flex: 1,
            minWidth: 110,
            valueGetter: (row) => moment(row?.date).format("MMM Do YY"),
          },
          {
            field: "type",
            headerName: "Type",
            flex: 1,
            minWidth: 80,
            renderCell: ({ row }) => (
              <Chip
                label={row?.offerSlotId ? "INCOME" : "RECEIVED"}
                color={row?.offerSlotId ? "success" : "error"}
                size="small"
              />
            ),
          },
          {
            field: "amount",
            headerName: "Amount",
            flex: 1,
            minWidth: 80,
          },
          {
            field: "currency",
            headerName: "Currency",
            flex: 1,
            minWidth: 80,
          },
          {
            field: "channel",
            headerName: "Channel",
            flex: 1.5,
            minWidth: 120,
            valueGetter: () => "Bank Alfalah",
          },
          {
            field: "transactionStatus",
            headerName: "Transaction Status",
            flex: 1.5,
            minWidth: 150,
            renderCell: ({ row }) => {
              return row?.status ? (
                <Chip
                  label={row?.status}
                  color={row?.status === "Success" ? "success" : "error"}
                  size="small"
                />
              ) : (
                "--"
              );
            },
          },
          {
            field: "student",
            headerName: "Student",
            flex: 1.5,
            minWidth: 150,
            renderCell: ({ row }) => row?.student || "--",
          },
          {
            field: "subjects",
            headerName: "Subjects",
            flex: 2,
            minWidth: 180,
            renderCell: ({ row }) => {
              return (
                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    justifyContent: "flex-start",
                    alignItems: "center",
                    width: "100%",
                    height: "100%",
                    padding: "4px",
                  }}
                >
                  {row?.subjects?.map((subj, index) => (
                    <Chip
                      key={index}
                      label={subj?.subjectName}
                      color="primary"
                      size="small"
                      sx={{ mr: 0.5, mb: 0.5 }}
                    />
                  ))}
                </Box>
              );
            },
          },
          {
            field: "sessionStatus",
            headerName: "Session Status",
            flex: 1.5,
            minWidth: 140,
            renderCell: ({ row }) => {
              return row?.sessionStatus ? (
                <Chip
                  label={row?.sessionStatus}
                  color={
                    row?.sessionStatus === "completed" ? "success" : "error"
                  }
                  size="small"
                />
              ) : (
                "--"
              );
            },
          },
        ];

      default:
        return [];
    }
  }, [tab]);

  const handleRowSelection = (selectionModel) => {
    const selectedIdsArray = Array.from(selectionModel.ids);
    const selectedRows = transactionsData.filter((row) =>
      selectedIdsArray.includes(row.id)
    );
    const totalAmount = selectedRows.reduce(
      (acc, row) => acc + Number(row.amount || 0),
      0
    );
    setTotalTransferAmount(totalAmount.toFixed(2));
    const offerSlotIds = selectedRows.map((row) => row.offerSlotId);
    setSelectedOfferSlotIds([...offerSlotIds]);
  };

  return transactionsData.length > 0 ? (
    <Box
      sx={{
        height: 400, // Set a specific height
        width: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <DataGrid
        rows={transactionsData}
        columns={columns}
        getRowId={(row) => row?.id}
        sx={{
          fontSize: "16px",
          flex: 1,
          width: "100%",
          "& .MuiDataGrid-root": {
            overflow: "auto",
          },
          "& .MuiDataGrid-virtualScroller": {
            overflow: "auto !important",
          },
        }}
        hideFooter
        hideFooterPagination
        checkboxSelection={tab === "inbound" && true}
        onRowSelectionModelChange={(newSelection) => {
          handleRowSelection(newSelection);
        }}
      />
    </Box>
  ) : (
    <Box sx={{ textAlign: "center", mt: 4 }}>
      <NoRecordsFound />
    </Box>
  );
};

export default TeacherWalletTable;
